import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ApiService } from 'src/app/shared/services/api.service';
import { BomMaterial, BomService } from 'src/app/shared/services/bom.service';

export interface GisOpsMapPoint {
  lon: number;
  lat: number;
  label: string;
  kind: 'attendance' | 'survey' | 'staff' | 'machine' | 'material';
  status?: string;
}

export interface GisOpsSurveyLrs {
  routeId: number;
  fromKm?: number;
  toKm?: number;
  chainage?: string;
}

export interface GisProjectOpsSnapshot {
  projectId: number;
  loadedAt: number;
  staff: any[];
  wbs: any[];
  surveys: any[];
  attendance: any[];
  machines: any[];
  materials: BomMaterial[];
  bomCode: string | null;
}

export type GisOpsMapAction =
  | { type: 'project' }
  | { type: 'points'; layer: 'biometric' | 'gps' | 'project'; points: GisOpsMapPoint[] }
  | { type: 'survey-lrs'; lrs: GisOpsSurveyLrs };

@Injectable({ providedIn: 'root' })
export class GisProjectOpsService {
  private cache: GisProjectOpsSnapshot | null = null;
  private inflight$: Observable<GisProjectOpsSnapshot> | null = null;
  private inflightId: number | null = null;

  constructor(
    private api: ApiService,
    private bom: BomService
  ) {}

  getSnapshot(projectId: number | string | null | undefined): GisProjectOpsSnapshot | null {
    const id = Number(projectId);
    if (!id || !this.cache || this.cache.projectId !== id) return null;
    return this.cache;
  }

  load(projectId: number | string | null | undefined, force = false): Observable<GisProjectOpsSnapshot> {
    const id = Number(projectId);
    if (!id) {
      this.cache = null;
      return of(this.emptySnapshot(0));
    }
    if (!force && this.cache?.projectId === id) {
      return of(this.cache);
    }
    if (!force && this.inflight$ && this.inflightId === id) {
      return this.inflight$;
    }

    const today = this.isoDate(new Date());
    const from = this.isoDate(this.addDays(new Date(), -13));

    this.inflightId = id;
    this.inflight$ = forkJoin({
      staff: this.api.getProjectStaffAllocation(id).pipe(
        map((res: any) => (res?.success ? res.data : res?.data) || []),
        catchError(() => of([]))
      ),
      wbs: this.api.getWBSByProject(id).pipe(
        map((res: any) => (res?.success ? res.data : res?.data) || []),
        catchError(() => of([]))
      ),
      surveys: this.api.listProjectSurveyStages({ project_id: id, limit: 50 }).pipe(
        map((res: any) => res?.data || []),
        catchError(() => of([]))
      ),
      attendance: this.api.getAttendance({
        projectId: String(id),
        userId: '',
        startDate: from,
        endDate: today,
      }, 'all').pipe(
        map((res: any) => (Array.isArray(res) ? res : (res?.data || []))),
        catchError(() => of([]))
      ),
      machines: this.api.getMachineReservations({ project_id: id, limit: 50 }).pipe(
        map((res: any) => this.unwrapList(res)),
        catchError(() => of([]))
      ),
      materials: this.loadMaterials(id),
    }).pipe(
      map((rows) => {
        const snapshot: GisProjectOpsSnapshot = {
          projectId: id,
          loadedAt: Date.now(),
          staff: Array.isArray(rows.staff) ? rows.staff : [],
          wbs: Array.isArray(rows.wbs) ? rows.wbs : [],
          surveys: Array.isArray(rows.surveys) ? rows.surveys : [],
          attendance: Array.isArray(rows.attendance) ? rows.attendance : [],
          machines: Array.isArray(rows.machines) ? rows.machines : [],
          materials: rows.materials.items,
          bomCode: rows.materials.bomCode,
        };
        this.cache = snapshot;
        return snapshot;
      }),
      tap({
        error: () => {
          this.inflight$ = null;
          this.inflightId = null;
        },
        complete: () => {
          this.inflight$ = null;
          this.inflightId = null;
        },
      }),
      shareReplay(1)
    );

    return this.inflight$;
  }

  private emptySnapshot(projectId: number): GisProjectOpsSnapshot {
    return {
      projectId,
      loadedAt: Date.now(),
      staff: [],
      wbs: [],
      surveys: [],
      attendance: [],
      machines: [],
      materials: [],
      bomCode: null,
    };
  }

  pointsFromAttendance(rows: any[]): GisOpsMapPoint[] {
    return this.extractPoints(rows, 'attendance', (row) =>
      row.full_name || row.user_name || row.employee_name || 'Staff'
    );
  }

  pointsFromSurveys(rows: any[]): GisOpsMapPoint[] {
    return this.extractPoints(rows, 'survey', (row) =>
      row.survey_lead || row.project_name || `Survey #${row.id || ''}`
    );
  }

  pointsFromStaff(rows: any[]): GisOpsMapPoint[] {
    return this.extractPoints(rows, 'staff', (row) =>
      row.full_name || [row.first_name, row.last_name].filter(Boolean).join(' ') || 'Staff'
    );
  }

  pointsFromMachines(rows: any[]): GisOpsMapPoint[] {
    return this.extractPoints(rows, 'machine', (row) =>
      row.machine_name || row.title || 'Machine'
    );
  }

  surveyLrs(row: any): GisOpsSurveyLrs | null {
    const remarks = this.parseJson(row?.remarks);
    const lrs = (remarks['lrs'] as Record<string, unknown>) || {};
    const routeId = Number(lrs['route_id']);
    if (!routeId) return null;
    const fromKm = lrs['from_km'] != null ? Number(lrs['from_km']) : undefined;
    const toKm = lrs['to_km'] != null ? Number(lrs['to_km']) : undefined;
    return {
      routeId,
      fromKm: Number.isFinite(fromKm) ? fromKm : undefined,
      toKm: Number.isFinite(toKm) ? toKm : undefined,
      chainage: lrs['chainage'] ? String(lrs['chainage']) : undefined,
    };
  }

  private loadMaterials(projectId: number) {
    return this.bom.getBoms({ project_id: projectId, limit: 8 }).pipe(
      switchMap((res) => {
        const list = res?.data || [];
        const pick = list.find((b) => b.status === 'approved') || list[0];
        if (!pick?.bom_id) {
          return of({ items: [] as BomMaterial[], bomCode: null as string | null });
        }
        return this.bom.getBom(pick.bom_id).pipe(
          map((detail) => ({
            items: detail?.data?.materials || [],
            bomCode: pick.bom_code || null,
          })),
          catchError(() => of({ items: [] as BomMaterial[], bomCode: pick.bom_code || null }))
        );
      }),
      catchError(() => of({ items: [] as BomMaterial[], bomCode: null as string | null }))
    );
  }

  private unwrapList(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.reservations)) return res.reservations;
    return [];
  }

  private extractPoints(
    rows: any[],
    kind: GisOpsMapPoint['kind'],
    labelFn: (row: any) => string
  ): GisOpsMapPoint[] {
    const points: GisOpsMapPoint[] = [];
    for (const row of rows || []) {
      const loc = this.readLatLon(row);
      if (!loc) continue;
      points.push({
        lon: loc.lon,
        lat: loc.lat,
        label: String(labelFn(row) || kind).trim() || kind,
        kind,
        status: row.status != null ? String(row.status) : undefined,
      });
    }
    return points;
  }

  private readLatLon(row: any): { lat: number; lon: number } | null {
    if (!row || typeof row !== 'object') return null;
    const lat = Number(
      row.latitude ?? row.lat ?? row.location_lat ?? row.geo_lat ?? row.location?.lat
    );
    const lon = Number(
      row.longitude ?? row.lon ?? row.lng ?? row.long ?? row.location_lng ?? row.geo_lng
      ?? row.location?.long ?? row.location?.lng
    );
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
    if (lat === 0 && lon === 0) return null;
    return { lat, lon };
  }

  private parseJson(value: unknown): Record<string, unknown> {
    if (!value) return {};
    if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
    if (typeof value !== 'string') return {};
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  private isoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private addDays(d: Date, days: number): Date {
    const next = new Date(d);
    next.setDate(next.getDate() + days);
    return next;
  }
}
